import { Box, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import saveAs from "file-saver";
import useAPI from "../../../../api/hooks/useAPI";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import GuidelineStep from "../components/GuidelineStep";
import microsoft_office_365_1 from "./images/microsoft_office_365_1.png";
import microsoft_office_365_2 from "./images/microsoft_office_365_2.png";

const MicrosoftOffice365GenerateKeyStep = ({ addToExpandedList }) => {
  const { generateMicrosoft365KMSkey, getMicrosoftOffice365ExistingKMSkey } =
    useAPI();

  const {
    data: existingKMSkey,
    isLoading: isExistingKMSkeyLoading,
    isSuccess: isExistingKMSkeySuccess,
  } = useQuery({
    queryKey: ["microsoft-office-365-existing-kms-key"],
    queryFn: getMicrosoftOffice365ExistingKMSkey,
    select: (data) => data.data,
    retry: false, // Don't retry on 404 - it means no existing key
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const {
    data: generatedKey,
    mutate: generateKey,
    isPending: isGeneratingKey,
  } = useMutation({
    mutationFn: generateMicrosoft365KMSkey,
    onSuccess: (response) => {
      Toast.success("Key generated successfully");
    },
    onError: (error) => {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    },
  });

  // Use existing key if available, otherwise use generated key
  const microsoft365UserPublicKey = existingKMSkey || generatedKey?.data || "";
  const hasExistingKey = isExistingKMSkeySuccess && existingKMSkey;
  const hasGeneratedKey = generatedKey?.data;

  const handleDownloadCertificate = () => {
    const blob = new Blob([microsoft365UserPublicKey], {
      type: "application/x-pem-file",
    });
    saveAs(blob, "BreezeCertificate.pem");
  };

  return (
    <GuidelineStep title="Generate Key">
      <Typography variant="body1">
        {hasExistingKey
          ? "Using existing RSA 4096 bit key pair."
          : "We'll generate a new pair of keys. (RSA 4096 bit keys)"}
      </Typography>
      {isExistingKMSkeyLoading ? (
        <LoadingIcon size={20} />
      ) : (
        <Box display="flex" flexDirection="column" gap={1}>
          <ContainedButton
            sxProps={{ width: "300px" }}
            color="primary"
            size="medium"
            loading={isGeneratingKey}
            disabled={hasExistingKey || hasGeneratedKey}
            onClick={async () => await generateKey()}
          >
            {hasExistingKey
              ? "Key already exists"
              : hasGeneratedKey
              ? "Key generated"
              : "Generate Public/Private key pair"}
          </ContainedButton>

          {/* Download button - enabled when we have a key (existing or generated) */}
          <ContainedButton
            style={{ width: "300px" }}
            color="primary"
            size="medium"
            onClick={handleDownloadCertificate}
            disabled={!microsoft365UserPublicKey}
          >
            Download Certificate
          </ContainedButton>
        </Box>
      )}
      <Typography variant="body1">
        Go to <b>Certificate and secrets</b> then click on <b>Certificates</b>{" "}
        and select <b>Upload certificate</b>{" "}
      </Typography>
      <ScreenshotAccordion
        images={[
          {
            file: microsoft_office_365_1,
            altext: "Microsoft Office 365 Generate Key",
          },
        ]}
        onExpanded={addToExpandedList}
      />
      <Typography variant="body1">
        Upload the certificate file you just downloaded.
      </Typography>
      <Typography variant="body1">
        Provide a description, for example <b>Breeze Security Platform</b>. And
        click <b>Add</b>
      </Typography>
      <ScreenshotAccordion
        images={[
          {
            file: microsoft_office_365_2,
            altext: "Microsoft Office 365 upload certificate",
          },
        ]}
        onExpanded={addToExpandedList}
      />
    </GuidelineStep>
  );
};

export default MicrosoftOffice365GenerateKeyStep;
